// The lobby's deckbuilder, minus the screen: the draft operations and everything the
// builder paints. Pure, so the copy and the limits sit under the coverage gate and the
// React component only binds them. A draft is {cards, openingHand}; every operation
// answers a new one.

import {CARD_POWER} from "./botPolicy.js";
import {
  DECK_SIZE,
  MAX_COPIES,
  OPENING_HAND_SIZE,
  STANDARD_CARD_IDS,
  countCards,
  fitOpeningHand,
  isValidDeck,
  standardDeck,
} from "./deckRules.js";

// The builder's tabs, in the order a deck gets thought about: what you hit with, what hits
// everyone, what stands in the way, what keeps you going, the tricks, and last the cards
// that need a partner. `tone` is the card effect whose colour the tab wears (the teamwork
// tab has its own). Thirty-nine cards in one list was the old builder's whole problem on
// a phone; a tab is a screen.
export const DECK_CATEGORIES = Object.freeze([
  { id: "attack", label: "Attack", title: "Attack", tone: "damage" },
  { id: "area", label: "Alla", title: "Alla andra", tone: "damage" },
  { id: "minion", label: "Minions", title: "Minions", tone: "shield" },
  { id: "guard", label: "Skydd", title: "Skydd & läk", tone: "heal" },
  { id: "trick", label: "Knep", title: "Knep", tone: "trick" },
  { id: "team", label: "Lag", title: "Lagkort", tone: "team" },
]);

// Derived rather than tagged, so a new card files itself. Whether a shield card raises a
// minion is read off the bot's table — test/bot-policy.test.cjs already proves those
// entries against every card's play() — instead of adding a fourth list to the card
// checklist. Snyltgästen raises its minion on an enemy, so it stays a trick. Teamwork
// cards go to their own tab whatever they do: what matters first is that they need a partner.
export function cardCategory(card) {
  if (card.teamOnly) return "team";
  if (card.effect === "damage") return card.target === "allEnemies" ? "area" : "attack";
  if (card.effect === "trick") return "trick";
  return CARD_POWER[card.id]?.shield === "minion" ? "minion" : "guard";
}

// The curve's last bar holds everything from six mana up.
const CURVE_TOP = 6;

// A first deck starts as the standard one, so there is something to swap cards out of.
// A saved deck that has gone stale — a card retired, a limit tightened — is trimmed to
// what is still legal rather than thrown away, so the player only has to top it up.
export function startDraft(saved, knownIds) {
  if (!saved) return { cards: standardDeck(), openingHand: [] };
  const known = new Set(knownIds);
  const tally = new Map();
  const cards = [];
  for (const id of saved.cards) {
    if (cards.length >= DECK_SIZE || !known.has(id) || (tally.get(id) || 0) >= MAX_COPIES) continue;
    tally.set(id, (tally.get(id) || 0) + 1);
    cards.push(id);
  }
  return { cards, openingHand: fitOpeningHand(saved.openingHand, cards) };
}

export function addCard(draft, id) {
  const copies = countCards(draft.cards).get(id) || 0;
  if (copies >= MAX_COPIES || draft.cards.length >= DECK_SIZE) return draft;
  return { ...draft, cards: [...draft.cards, id] };
}

// One copy out; a pick the deck can no longer supply goes with it.
export function removeCard(draft, id) {
  const index = draft.cards.lastIndexOf(id);
  if (index < 0) return draft;
  const cards = draft.cards.filter((_, position) => position !== index);
  return { cards, openingHand: fitOpeningHand(draft.openingHand, cards) };
}

// Tapping a card's star picks one more copy for the opening hand, until the deck has no
// more copies of it — then the next tap puts them all back. A full hand takes no more.
export function togglePick(draft, id) {
  const copies = countCards(draft.cards).get(id) || 0;
  const picked = countCards(draft.openingHand).get(id) || 0;
  if (picked < copies && draft.openingHand.length < OPENING_HAND_SIZE) {
    return { ...draft, openingHand: [...draft.openingHand, id] };
  }
  return { ...draft, openingHand: draft.openingHand.filter((pick) => pick !== id) };
}

// A start-hand slot clears the one copy it shows; togglePick would put back every copy.
export function unpickAt(draft, index) {
  if (index < 0 || index >= draft.openingHand.length) return draft;
  return { ...draft, openingHand: draft.openingHand.filter((_, position) => position !== index) };
}

function sameCards(left, right) {
  return [...left].sort().join("\n") === [...right].sort().join("\n");
}

export function draftChanged(draft, base) {
  return !sameCards(draft.cards, base.cards) || !sameCards(draft.openingHand, base.openingHand);
}

function sameDeck(draft, saved) {
  return Boolean(saved) && !draftChanged(draft, saved);
}

function hintText(count, changed, status) {
  if (status === "failed") return "Kunde inte spara leken.";
  if (status === "saved" && !changed) return "Leken sparades.";
  if (count < DECK_SIZE) return `Lägg till ${DECK_SIZE - count} kort till.`;
  return "Leken är komplett.";
}

function manaCurve(cards, byId) {
  const counts = Array(CURVE_TOP + 1).fill(0);
  cards.forEach((id) => {
    const card = byId.get(id);
    if (card) counts[Math.min(card.cost, CURVE_TOP)] += 1;
  });
  const peak = Math.max(1, ...counts);
  return counts.map((count, cost) => ({ cost: cost === CURVE_TOP ? `${cost}+` : String(cost), count, share: count / peak }));
}

// Four slots, the picks first in the order they were made. `index` is the pick's place
// in the draft, which is what unpickAt takes.
function openingSlots(openingHand, byId) {
  const picks = openingHand.map((id, index) => ({ index, card: byId.get(id) })).filter((slot) => slot.card);
  return Array.from({ length: OPENING_HAND_SIZE }, (_, position) => {
    const pick = picks[position];
    return pick
      ? { ...pick, label: `Ta bort ${pick.card.name} ur starthanden` }
      : { index: null, card: null, label: "Tom plats i starthanden" };
  });
}

// status is the controller's save state: null, "saving", "saved" or "failed". tab and
// onlyInDeck are what the builder is showing; closeArmed is the two-tap close.
export function deckBuilderView({ draft, saved, library, status, tab, onlyInDeck = false, closeArmed = false }) {
  const copies = countCards(draft.cards);
  const picks = countCards(draft.openingHand);
  const handFull = draft.openingHand.length >= OPENING_HAND_SIZE;
  const byId = new Map(library.map((card) => [card.id, card]));
  const allRows = library.map((card) => {
    const count = copies.get(card.id) || 0;
    const picked = picks.get(card.id) || 0;
    return {
      card,
      category: cardCategory(card),
      count,
      picked,
      pips: Array.from({ length: MAX_COPIES }, (_, copy) => copy < count),
      isNew: !STANDARD_CARD_IDS.includes(card.id),
      canAdd: count < MAX_COPIES && draft.cards.length < DECK_SIZE,
      canRemove: count > 0,
      canPick: count > 0 && (picked > 0 || !handFull),
      stars: picked ? Array(picked).fill(true) : [false],
    };
  });
  const active = DECK_CATEGORIES.find((category) => category.id === tab) || DECK_CATEGORIES[0];
  const inCategory = (id) => allRows.filter((row) => row.category === id);
  const copiesIn = (rows) => rows.reduce((total, row) => total + row.count, 0);
  const shown = inCategory(active.id);
  const rows = onlyInDeck ? shown.filter((row) => row.count > 0) : shown;
  const curve = manaCurve(draft.cards, byId);
  const changed = !sameDeck(draft, saved);
  const legal = isValidDeck(draft.cards, library.map((card) => card.id));
  return {
    rows,
    tabs: DECK_CATEGORIES.map((category) => ({
      ...category,
      inDeck: copiesIn(inCategory(category.id)),
      active: category.id === active.id,
    })),
    activeTab: active.id,
    tone: active.tone,
    sectionTitle: active.title,
    sectionLine: `${shown.length} olika · ${copiesIn(shown)} i leken`,
    onlyInDeck,
    filterLabel: "Bara i leken",
    emptyText: rows.length ? null : `Du har inga kort från ${active.title} i leken.`,
    curve,
    curveLabel: `Manakurva: ${curve.map((bar) => `${bar.cost} mana ${bar.count}`).join(", ")}`,
    slots: openingSlots(draft.openingHand, byId),
    countLabel: `${draft.cards.length}/${DECK_SIZE} kort`,
    openingLabel: `Starthand ${draft.openingHand.length}/${OPENING_HAND_SIZE}`,
    canSave: legal && changed && status !== "saving",
    saveLabel: status === "saving" ? "Sparar…" : "Spara lek",
    closeArmed,
    closeLabel: closeArmed ? "Släng ändringar?" : "Stäng",
    hint: hintText(draft.cards.length, changed, status),
  };
}
